"use client";

import { useState, useMemo, useCallback } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Dialog, DialogTrigger, DialogContent, DialogClose, DialogTitle } from "@perceptron/ui/components/dialog";
import { Icon } from "@perceptron/ui/components/icon";
import { toast } from "sonner";

import { analytics, ANALYTICS_EVENTS } from "@demo/libs/analytics";

import { exportCodeColors } from "./colors";
import { SyntaxHighlighter, atomOneDark } from "./highlighters";
import { type ExportDialogShellProps } from "./types";

/**
 * Presentational export-code dialog: API-key step, language tabs, syntax-
 * highlighted snippet, copy-to-clipboard. Mode-agnostic and store-free — each
 * mode supplies its strings, languages, and a `getCode` closure (which reads its
 * own stores + generators). Dispatch lives in `ExportButton` + `registry`.
 */
const ExportDialogShell = ({
  title,
  description,
  languages,
  getCode,
  showApiKeyStep = true,
  analyticsModel,
}: ExportDialogShellProps) => {
  const [open, setOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]?.value ?? "");
  const [apiKey, setApiKey] = useState("");
  const [isApiKeyFocused, setIsApiKeyFocused] = useState(false);

  const highlighter = languages.find((lang) => lang.value === selectedLanguage)?.highlighter ?? "text";

  // Build code only while the dialog is open. `getCode` is stable by contract.
  const generatedCode = useMemo(() => {
    if (!open) return null;
    return getCode(apiKey, selectedLanguage);
  }, [open, apiKey, selectedLanguage, getCode]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) {
        analytics.track(ANALYTICS_EVENTS.EXPORT_CODE_OPENED, { model: analyticsModel?.() });
      }
    },
    [analyticsModel],
  );

  const handleLanguageChange = useCallback(
    (language: string) => {
      setSelectedLanguage(language);
      analytics.track(ANALYTICS_EVENTS.EXPORT_CODE_LANGUAGE_CHANGED, { language, model: analyticsModel?.() });
    },
    [analyticsModel],
  );

  const handleCopyToClipboard = useCallback(async () => {
    if (!generatedCode) return;
    try {
      await navigator.clipboard.writeText(generatedCode);
      analytics.track(ANALYTICS_EVENTS.EXPORT_CODE_COPIED, { language: selectedLanguage, model: analyticsModel?.() });
      toast.success(strings.exportCode.toast.copySuccess);
    } catch {
      toast.error(strings.exportCode.toast.copyError);
    }
  }, [generatedCode, selectedLanguage, analyticsModel]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-1 p-3" variant="ghost">
          <Icon className="size-4 text-foreground-primary" name="code" />
          <span className="font-medium text-foreground-primary label-2">{strings.exportCode.buttonLabel}</span>
        </Button>
      </DialogTrigger>
      <DialogContent
        className="flex h-[600px] max-w-[850px] gap-0 overflow-hidden rounded-2xl border-none bg-background-primary px-2 py-2 shadow-lg"
        hideCloseButton
      >
        <DialogTitle className="sr-only">{strings.exportCode.ariaLabels.dialogTitle}</DialogTitle>

        {/* Left Panel - Sidebar */}
        <div className="flex w-[282px] shrink-0 flex-col justify-between rounded-l-2xl bg-background-primary py-6 pl-4 pr-6">
          <Icon className="size-8 text-foreground-primary" name="code-xml" />
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold leading-6 text-foreground-primary">{title}</h2>
            <p className="text-sm leading-5 text-foreground-primary">{description}</p>
          </div>
        </div>

        {/* Right Panel - Content */}
        <div className="flex min-w-0 flex-1 flex-col gap-12 overflow-hidden rounded-lg bg-background-alt-2 p-8">
          {/* Step 1 - API Key */}
          {showApiKeyStep && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-6">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-hover p-1">
                  <span className="text-base font-semibold leading-6 text-foreground-primary">1</span>
                </div>
                <p className="text-sm leading-5 text-foreground-primary">
                  {strings.exportCode.steps.apiKey.text}{" "}
                  <span className="text-foreground-secondary">{strings.exportCode.steps.apiKey.optional}</span>
                </p>
              </div>
              <div className="pl-14">
                <div className="dark relative overflow-clip rounded-md bg-background-alt-2 ring-white/30 focus-within:ring-1">
                  <SyntaxHighlighter
                    customStyle={{
                      margin: 0,
                      padding: "0.5rem 0.75rem",
                      background: "transparent",
                      fontSize: "0.75rem",
                      lineHeight: "1.5",
                    }}
                    language="python"
                    style={atomOneDark}
                  >
                    {`api_key="${apiKey || (isApiKeyFocused ? "" : "<your_api_key_here>")}"`}
                  </SyntaxHighlighter>
                  <input
                    className="absolute inset-0 bg-transparent px-[4.85rem] py-2 font-mono text-xs leading-[1.5] text-transparent caret-white focus:outline-none"
                    type="text"
                    value={apiKey}
                    onBlur={() => setIsApiKeyFocused(false)}
                    onChange={(e) => setApiKey(e.target.value)}
                    onFocus={() => setIsApiKeyFocused(true)}
                  />
                  <div className="pointer-events-none absolute inset-0 shadow-[inset_0px_0px_12px_6px_black]" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 - Language Selection & Code */}
          <div className="flex min-h-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-6">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-hover p-1">
                <span className="text-base font-semibold leading-6 text-foreground-primary">2</span>
              </div>
              <p className="text-sm leading-5 text-foreground-primary">
                {strings.exportCode.steps.selectLanguage.text}
              </p>
            </div>
            <div className="min-h-0 flex-1 pl-14">
              <div className="dark flex h-full flex-col overflow-hidden rounded-2xl bg-background-alt-2">
                {/* Language Tabs */}
                <div className="flex shrink-0 items-center border-b border-border bg-background-hover px-5 py-2">
                  {languages.map((lang) => (
                    <button
                      className="px-3 py-2 text-sm leading-5 transition-colors"
                      key={lang.value}
                      style={{
                        color: selectedLanguage === lang.value ? exportCodeColors.text : exportCodeColors.textSecondary,
                        borderBottom: selectedLanguage === lang.value ? `1px solid ${exportCodeColors.text}` : "none",
                      }}
                      type="button"
                      onClick={() => handleLanguageChange(lang.value)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                {/* Code Block */}
                <div className="flex-1 overflow-auto dark:bg-black">
                  <SyntaxHighlighter
                    customStyle={{
                      margin: 0,
                      padding: "2rem",
                      background: "transparent",
                      fontSize: "0.75rem",
                      lineHeight: "1.5",
                    }}
                    language={highlighter}
                    style={atomOneDark}
                  >
                    {generatedCode ?? ""}
                  </SyntaxHighlighter>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 - Copy to Clipboard */}
          <div className="flex items-center gap-6">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-hover p-1">
              <span className="text-base font-semibold leading-6 text-foreground-primary">3</span>
            </div>
            <button
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm leading-5"
              style={{
                backgroundColor: exportCodeColors.buttonBackground,
                border: `1px solid ${exportCodeColors.buttonBorder}`,
                color: exportCodeColors.text,
              }}
              type="button"
              onClick={handleCopyToClipboard}
            >
              {strings.exportCode.steps.copy.button}
              <Icon className="size-4" name="copy" />
            </button>
          </div>
        </div>

        {/* Close Button */}
        <DialogClose
          aria-label={strings.exportCode.ariaLabels.closeButton}
          className="absolute right-4 top-4 text-foreground opacity-70 transition-opacity hover:opacity-100"
        >
          <Icon className="size-6" name="x" />
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialogShell;
